import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { MyProductReview } from '../../../../../music24-api/src/productReviews/productReviewTypes';
import { useTRPCAPI, useTRPCClientAPI, useTRPCMutation } from '../../../trpc/trpc';
import type { ReviewSubmitInput } from './reviewRules';

/** The kind of a failed send: `refused` = the server answered BAD_REQUEST, else `failed`. */
export type ReviewSendError = 'refused' | 'failed';

function sendErrorOf(error: unknown): ReviewSendError | null {
  if (error === null || error === undefined) {
    return null;
  }
  const code =
    typeof error === 'object' && 'data' in error && error.data !== null && typeof error.data === 'object'
      ? (error.data as { code?: unknown }).code
      : undefined;
  return code === 'BAD_REQUEST' ? 'refused' : 'failed';
}

/**
 * `reviews.submit`: makes or edits the review of the user. On success the cached `reviews.mine`
 * answer takes the saved review, so the edit state shows the new status at once.
 */
export function useSubmitReview(onSaved: (review: MyProductReview) => void) {
  const trpcAPI = useTRPCAPI();
  const trpcClient = useTRPCClientAPI();
  const queryClient = useQueryClient();

  const mutation = useTRPCMutation({
    mutationFn: (input: ReviewSubmitInput) => trpcClient.reviews.submit.mutate(input),
    onSuccess: (answer: { review: MyProductReview }) => {
      onSaved(answer.review);
      queryClient.invalidateQueries({ queryKey: trpcAPI.reviews.mine.queryKey() }).catch(console.error);
    },
  });

  const { mutate, reset } = mutation;
  const submit = useCallback(
    (input: ReviewSubmitInput) => {
      reset();
      mutate(input);
    },
    [mutate, reset]
  );

  return { submit, isPending: mutation.isPending, sendError: sendErrorOf(mutation.error) };
}
